import React from 'react';
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
  convertFromRaw
} from 'draft-js';
import { StyleSheet, View, Text, Link, Font } from '@react-pdf/renderer';
// @ts-ignore
import redraft from 'redraft';

// Remove font registration completely - let PdfOfferta handle it to avoid conflicts
// This prevents duplicate registrations that cause font resolution errors

const styles = StyleSheet.create({
  headingOne: {
    //marginBottom: 4,
    color: '#324d67',
    fontFamily: 'Open Sans',
    fontWeight: "bold", // ✅ matches PdfOfferta's registration
    lineHeight: 1.35,
    fontSize: 7,
  },
  text: {
    //marginBottom: 8,
    color: '#324d67',
    fontFamily: 'Open Sans',
    // no fontWeight - matches PdfOfferta's regular font registration
    fontSize: 7,
    lineHeight: 1.45,
  },
  list: {
   // marginBottom: 8,
    marginLeft: 6,
  },
  listItem: {
    //marginBottom: 4,
  },
  listItemText: {
    color: '#324d67',
    fontFamily: 'Open Sans',
    // no fontWeight - matches PdfOfferta's regular font registration
    fontSize: 7,
    lineHeight: 1.45,
  },
});

const HeadingOne = ({ children }) => {
  return (
    <View>
      <Text style={styles.headingOne}>{children}</Text>
    </View>
  );
};

const UnorderedList = ({ children, depth }) => {
  return <View style={styles.list}>{children}</View>;
};

const UnorderedListItem = ({ children }) => {
  return (
    <View style={styles.listItem}>
      <Text style={styles.listItemText}>
        • &nbsp;<Text>{children}</Text>
      </Text>
    </View>
  );
};

const OrderedList = ({ children, depth }) => {
  return <View style={styles.list}>{children}</View>;
};

const OrderedListItem = ({ children, index }) => {
  return (
    <View style={styles.listItem}>
      <Text style={styles.listItemText}>
        {index + 1}. &nbsp;<Text>{children}</Text>
      </Text>
    </View>
  );
};

const renderers = {
  inline: {
    // The key passed here is just an index based on rendering order inside a block
    BOLD: (children, { key }) => {
      return (
        <Text key={`bold-${key}`} style={{ fontWeight: "bold" }}>
          {children}
        </Text>
      );
    },
    ITALIC: (children, { key }) => {
      return (
        <Text key={`italic-${key}`} style={{ fontStyle: 'italic' }}>
          {children}
        </Text>
      );
    },
    UNDERLINE: (children, { key }) => {
      return (
        <Text key={`underline-${key}`} style={{ textDecoration: 'underline' }}>
          {children}
        </Text>
      );
    },
  },
  /**
   * Blocks receive children and depth
   * Note that children are an array of blocks with same styling,
   */
  blocks: {
    unstyled: (children, { keys }) => {
      return children.map?.((child, index) => {
        return (
          <View key={keys[index]}>
            <Text style={{...styles.text, fontFamily: 'Open Sans'}}>{child}</Text>
          </View>
        );
      });
    },
    'header-one': (children, { keys }) => {
      return children.map?.((child, index) => {
        return <HeadingOne key={keys[index]}>{child}</HeadingOne>;
      });
    },
    'unordered-list-item': (children, { depth, keys }) => {
      return (
        <UnorderedList key={keys[keys.length - 1]} depth={depth}>
          {children.map?.((child, index) => (
            <UnorderedListItem key={keys[index]}>{child}</UnorderedListItem>
          ))}
        </UnorderedList>
      );
    },
    'ordered-list-item': (children, { depth, keys }) => {
      return (
        <OrderedList key={keys.join('|')} depth={depth}>
          {children.map?.((child, index) => (
            <OrderedListItem key={keys[index]} index={index}>
              {child}
            </OrderedListItem>
          ))}
        </OrderedList>
      );
    },
  },
  /**
   * Entities receive children and the entity data
   */
  entities: {
    // key is the entity key value from raw
    LINK: (children, data, { key }) => (
      <Link key={key} src={data.url}>
        {children}
      </Link>
    ),
  },
};

// Error boundary component for catching font resolution errors
class RedraftErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 RedraftEditor FONT ERROR - PDF generation failed:');
    console.error('Error details:', error);
    console.error('Error info:', errorInfo);
    console.error('Problematic document data:', JSON.stringify(this.props.existingDoc, null, 2));
    console.error('Document blocks:', this.props.existingDoc?.blocks);
    console.error('Document entity map:', this.props.existingDoc?.entityMap);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Text style={{...styles.text, fontFamily: 'Times-Roman'}}>
          {this.props.existingDoc?.blocks?.[0]?.text || '[Content unavailable due to formatting issue]'}
        </Text>
      );
    }

    return this.props.children;
  }
}

const RedraftEditor = ({ existingDoc }) => {
  if(!existingDoc) return ""
  
  // Always log document data first - BEFORE any early returns
  const documentKey = existingDoc?.blocks?.[0]?.key;
  
  // Set current document for font error tracking BEFORE processing (browser only)
  if (typeof window !== 'undefined' && window.setCurrentProcessingDocument) {
    window.setCurrentProcessingDocument({
      documentKey: documentKey || 'no-key',
      firstBlockText: existingDoc?.blocks?.[0]?.text?.substring(0, 100) + '...',
      fullDocument: existingDoc,
      timestamp: Date.now()
    });
  }
  
  // Update PDF generation progress bar
  if (typeof window !== 'undefined' && window.updatePdfProgress) {
    window.updatePdfProgress({
      documentKey: documentKey || 'no-key',
      firstBlockText: existingDoc?.blocks?.[0]?.text?.substring(0, 50) + '...',
      timestamp: Date.now()
    });
  }
  
  // Commented out detailed logging for cleaner console - uncomment for debugging
  // console.log('🔍 RedraftEditor processing document:', {
  //   hasBlocks: !!existingDoc?.blocks,
  //   blockCount: existingDoc?.blocks?.length,
  //   hasEntityMap: !!existingDoc?.entityMap,
  //   entityCount: Object.keys(existingDoc?.entityMap || {}).length,
  //   firstBlockText: existingDoc?.blocks?.[0]?.text?.substring(0, 50) + '...',
  //   documentId: documentKey || 'no-key'
  // });
  
  // Check for any inline styles that might cause font issues
  const inlineStyles = existingDoc?.blocks?.flatMap(block => 
    block?.inlineStyleRanges?.map(range => range.style) || []
  );
  
  if (inlineStyles.length > 0) {
    // Commented out style logging - uncomment for debugging
    // console.log('🎨 Document inline styles found:', inlineStyles);
    // console.log('📝 Detailed style ranges:', styleRanges);
    
    // Check if this is one of the problematic documents - keep critical error logging
    const isProblematicDoc = existingDoc?.blocks?.[0]?.text?.includes('Ministero del Lavoro') ||
                            existingDoc?.blocks?.[0]?.text?.includes('Manodopera Operatore tecnico');
    
    if (isProblematicDoc) {
      console.error('⚠️ POTENTIALLY PROBLEMATIC DOCUMENT DETECTED:', {
        documentKey: documentKey,
        suspiciousPattern: 'This document has caused font errors before'
      });
    }
  }
  
  // Check if this is the problematic document and use safe fallback AFTER logging
  const isProblematicDocument = documentKey === 'e90e7d6af01a42c5a20079e4f91a2ff9' || 
                                existingDoc?.blocks?.[0]?.text?.includes('Manodopera Operatore tecnico');
  
  if (isProblematicDocument) {
    console.log('🛡️ USING SAFE FALLBACK for problematic document:', documentKey);
    
    // Still update progress for problematic documents
    if (typeof window !== 'undefined' && window.updatePdfProgress) {
      window.updatePdfProgress({
        documentKey: documentKey || 'no-key',
        firstBlockText: '[Safe fallback] ' + (existingDoc?.blocks?.[0]?.text?.substring(0, 50) + '...' || 'Problematic document'),
        timestamp: Date.now()
      });
    }
    
    // Render problematic document with Times-Roman to avoid font issues
    return (
      <View>
        <Text style={{ fontFamily: 'Times-Roman', fontSize: 7, color: '#324d67' }}>
          {existingDoc?.blocks?.[0]?.text || '[Problematic content rendered safely]'}
        </Text>
      </View>
    );
  }
  
  const editorState = EditorState?.createWithContent(convertFromRaw(existingDoc));
  const rawContent = convertToRaw(editorState.getCurrentContent());
  
  return (
    <RedraftErrorBoundary existingDoc={existingDoc}>
      {redraft(rawContent, renderers, { blockFallback: 'unstyled' })}
    </RedraftErrorBoundary>
  );
};

export default RedraftEditor;
